import { useEffect, useMemo, useState } from "react";
import "./GitHubProfileCard.css";

function normalizeUrl(url) {
  if (!url) {
    return "";
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  return `https://${url}`;
}

function GitHubProfileCard({ username, width = 760, height = 320 }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchGitHubUser() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `https://api.github.com/users/${username}`,
          {
            signal: abortController.signal,
          },
        );

        if (!response.ok) {
          throw new Error("Failed to load GitHub user");
        }

        const userData = await response.json();
        setUser(userData);
      } catch (fetchError) {
        if (fetchError.name !== "AbortError") {
          setError(fetchError.message || "Failed to load GitHub user");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchGitHubUser();

    return () => {
      abortController.abort();
    };
  }, [username]);

  const cardStyle = useMemo(
    () => ({
      maxWidth: `${width}px`,
      minHeight: `${height}px`,
    }),
    [height, width],
  );

  if (loading) {
    return (
      <div
        className="github-profile-card github-profile-card--state"
        style={cardStyle}
      >
        <p className="github-profile-card__status">Loading GitHub profile...</p>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div
        className="github-profile-card github-profile-card--state"
        style={cardStyle}
      >
        <p className="github-profile-card__status">
          Error: {error || "Unknown error"}
        </p>
      </div>
    );
  }

  const profileUrl = `https://github.com/${user.login}`;
  const linkedInUrl = "https://www.linkedin.com/in/lars-hamburger-263a85323/";
  const blogUrl = normalizeUrl(user.blog || "");

  return (
    <article className="github-profile-card" style={cardStyle}>
      <div className="github-profile-card__sidebar">
        <img
          src={user.avatar_url}
          alt={`${user.login} avatar`}
          className="github-profile-card__avatar"
        />
        <h3 className="github-profile-card__name">{user.name || user.login}</h3>
        <p className="github-profile-card__login">@{user.login}</p>
        <div className="github-profile-card__actions">
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="github-profile-card__button"
          >
            GitHub ↗
          </a>
          <a
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="github-profile-card__button"
          >
            LinkedIn ↗
          </a>
        </div>
      </div>

      <div className="github-profile-card__divider" aria-hidden="true" />

      <div className="github-profile-card__content">
        {user.bio ? (
          <p className="github-profile-card__bio">{user.bio}</p>
        ) : null}

        {user.location ? (
          <p className="github-profile-card__meta">📍 {user.location}</p>
        ) : null}

        {user.blog ? (
          <p className="github-profile-card__meta">
            🔗
            <a href={blogUrl} target="_blank" rel="noopener noreferrer">
              {user.blog}
            </a>
          </p>
        ) : null}
      </div>
    </article>
  );
}

export default GitHubProfileCard;
